import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { api } from "../../api";

const BLANK = { key: "", label: "", blurb: "", image: "", order: 10 };

const AdminSections = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/sections").then((r) => setSections(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(BLANK); };
  const openEdit = (s) => { setEditing(s.key); setForm({ ...s }); };
  const close = () => setEditing(null);

  const save = async () => {
    setBusy(true);
    const payload = { ...form, order: Number(form.order) };
    try {
      if (editing === "new") await api.post("/admin/sections", payload);
      else await api.put(`/admin/sections/${editing}`, payload);
      await load(); close();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (key) => { if (window.confirm("Delete this section?")) { await api.delete(`/admin/sections/${key}`); load(); } };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="font-display text-3xl font-bold uppercase text-[#1B2a21]">Sections</h1><p className="text-[#6b7d72]">Add sections & change their photos</p></div>
        <button onClick={openNew} className="flex items-center gap-2 rounded-full bg-[#2D6A4F] px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#1B4332]"><Plus size={18} /> Add Section</button>
      </div>

      {loading ? <Loader2 className="animate-spin text-[#2D6A4F]" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sections.map((s) => (
            <div key={s.key} className="overflow-hidden rounded-2xl border border-[#e6f0ea] bg-white">
              <div className="relative aspect-[16/9] bg-[#f3f8f4]">
                <img src={s.image} alt={s.label} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <span className="absolute bottom-3 left-3 font-display text-lg font-bold uppercase text-white">{s.label}</span>
              </div>
              <div className="flex items-center justify-between p-4">
                <div><p className="text-sm text-[#6b7d72]">{s.blurb}</p><p className="mt-1 text-xs text-[#8aa093]">key: {s.key} · order: {s.order}</p></div>
                <div className="flex gap-1"><button onClick={() => openEdit(s)} className="rounded-lg p-2 text-[#2D6A4F] hover:bg-[#f0f7f2]"><Pencil size={16} /></button><button onClick={() => del(s.key)} className="rounded-lg p-2 text-[#c1121f] hover:bg-[#fbe9e9]"><Trash2 size={16} /></button></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <div className="relative w-full max-w-md rounded-3xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-display text-xl font-bold uppercase text-[#1B4332]">{editing === "new" ? "Add Section" : "Edit Section"}</h2><button onClick={close}><X size={22} className="text-[#1B4332]" /></button></div>
            <div className="space-y-3">
              {form.image && <img src={form.image} alt="" className="h-32 w-full rounded-xl object-cover" />}
              <F label="Key (unique, no spaces)" value={form.key} disabled={editing !== "new"} onChange={(v) => setForm({ ...form, key: v })} />
              <F label="Label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} />
              <F label="Blurb" value={form.blurb} onChange={(v) => setForm({ ...form, blurb: v })} />
              <F label="Image URL (photo)" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
              <F label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
              <button onClick={save} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2D6A4F] py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#1B4332] disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, value, onChange, type = "text", disabled }) => (
  <div><label className="mb-1 block font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">{label}</label>
    <input type={type} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-[#dceadf] px-4 py-2.5 text-sm outline-none focus:border-[#52B788] disabled:bg-[#f0f5f2]" /></div>
);

export default AdminSections;
